import { memo, useEffect, useState } from "react";
import {
  Accordion,
  ActionIcon,
  Box,
  Flex,
  Text,
  Title,
  useMantineTheme,
  Transition,
  CloseButton,
} from "@mantine/core";
import { IconCirclePlus, IconPencil } from "@tabler/icons";
import {
  GetSignedAgreementsQuery,
  useDeleteRecurringChargeMutation,
  GetSignedAgreementQuery,
  UpdateScheduledChargesInput,
  useDeleteScheduledTransactionMutation,
  PaymentMethodType,
  AlterationAgreementType,
  PaymentFrequency,
  SignedAgreementStatus,
  useUpdateSignedAgreementMutation,
  useGetAgreementRequestsLazyQuery,
  AlterationAgreementStatus,
} from "src/generated/graphql";
import { AiOutlinePauseCircle } from "react-icons/ai";
import { MdPlaylistAdd } from "react-icons/md";
import { RxDotsVertical } from "react-icons/rx";
import { BsThreeDotsVertical } from "react-icons/bs";
import { BiBlock } from "react-icons/bi";
import CreateSignedAgreement from "./CreateSignedAgreement";
import { useModals } from "@mantine/modals";
import { useParams } from "react-router-dom";
import { useClickOutside } from "@mantine/hooks";
import EditSignedAgreement, { EditModalsList } from "./EditSignedAgreement";
import SignedAgreementRecurringCharge from "./EditSignedAgreement/RecurringCharges";
import { CRUDSTATES, EMPLOYEE_ROLE_OBJ } from "src/utils/constants";
import ViewPolicies from "../../../../../components/SignNowDocs/ViewPolicies";
import { currencyFormatter } from "src/utils/formatting";
import SchedulePayment from "src/modules/Client/ScheduledPayments";
import UpcomingPayments from "./UpcomingPayments";
import Policies from "./Policies";
import StatusChangeRequest from "./EditSignedAgreement/StatusChangeRequest";
import useWritePermission from "src/hooks/usePermission";
import { useAppContext } from "src/api/AppContext";

interface ClientOverProps {
  agreementData: GetSignedAgreementsQuery["signedAgreementMany"];
  onRefetch: () => void;
}

type IEditProps = {
  modalId: string;
  modalName: string;
  agreement?: GetSignedAgreementQuery["signedAgreementOne"];
};

type ISchecduledPayment = {
  state: CRUDSTATES;
  subscriptionId: string;
  scheduledTransactionId?: string;
  transactionDate?: Date;
  chargeData?: UpdateScheduledChargesInput;
};

const Agreements = ({ agreementData, onRefetch }: ClientOverProps) => {
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.clients);
  const { currentLocation } = useAppContext();
  const theme = useMantineTheme();
  const modals = useModals();
  const { clientId } = useParams<{ clientId: string }>();
  const [activeOption, setActiveOption] = useState<
    { name: string; id: string } | undefined
  >(undefined);
  const outsideClickRef = useClickOutside(() => {
    setActiveOption(undefined);
  });

  let color = theme.colors.secondaryTextColor[2];

  const [getAgreementRequests, { data: agreementRequests }] =
    useGetAgreementRequestsLazyQuery();

  const [updateAgreement, {}] = useUpdateSignedAgreementMutation({
    onCompleted: () => {
      onRefetch();
      fetchAgreementRequests();
      modals.closeAll();
    },
  });

  const updateAgreementStatus = (signedAgreementId: string) => {
    updateAgreement({
      variables: {
        id: signedAgreementId,
        data: {
          status: SignedAgreementStatus.Active,
        },
      },
    });
  };

  const handleSignedAgreementModal = () => {
    modals.openModal({
      children: (
        <CreateSignedAgreement
          clientId={clientId || ""}
          agreementData={agreementData}
          onRefetch={onRefetch}
        />
      ),
      withCloseButton: false,
      closeOnClickOutside: false,
      styles: {
        modal: {
          width: "auto",
          minWidth: "440px",
        },
        body: {
          // minHeight: "500px",
          // padding: 0,
          // //backgroundColor: theme.colors.secondary[6],
          minHeight: "fit-content",
        },
      },
    });
  };

  const handleOpenListMenu = (option: { name: string; id: string }) => {
    setActiveOption(option);
  };

  const updateSignedAgreementStatus = (
    id: string,
    locationId: string,
    status: AlterationAgreementType,
    paymentFrequency: PaymentFrequency
  ) => {
    const requestHeader =
      status === AlterationAgreementType.Freeze
        ? "Freeze Agreement Request"
        : "Cancel Agreement Request";
    modals.openModal({
      withCloseButton: true,
      closeOnClickOutside: true,
      title: requestHeader,
      children: (
        <StatusChangeRequest
          id={id}
          status={status}
          locationId={locationId}
          clientId={clientId!}
          closeModal={() => modals.closeAll()}
          onRefetch={() => {
            onRefetch();
            fetchAgreementRequests();
          }}
          paymentFrequency={paymentFrequency}
        />
      ),
      styles: {
        modal: {
          width: "auto",
          minWidth: "440px",
        },
        body: {
          minHeight: "fit-content",
        },
      },
    });
  };

  const [deleteRecurringCharge, { loading: deleteLoading }] =
    useDeleteRecurringChargeMutation({
      onCompleted: () => {
        onRefetch();
      },
    });

  const [deleteScheduleTransaction, {}] = useDeleteScheduledTransactionMutation(
    {
      onCompleted: () => {
        onRefetch();
      },
    }
  );

  const handleEditModal = ({ modalId, modalName, agreement }: IEditProps) => {
    modals.openModal({
      closeOnClickOutside: false,
      title: modalName,
      children: (
        <EditSignedAgreement
          modalId={modalId}
          clientId={clientId}
          agreement={agreement}
          onRefetch={onRefetch}
        />
      ),
    });
  };

  const handleRecurringChargesModal = (
    state: string,
    subscriptionId: string,
    recurringChargeId?: string
  ) => {
    modals.openModal({
      closeOnClickOutside: false,
      title: `${state} Recurring Charge`,
      children: (
        <SignedAgreementRecurringCharge
          state={state}
          subscriptionId={subscriptionId}
          recurringChargeId={recurringChargeId}
          onRefetch={onRefetch}
        />
      ),
    });
  };

  const handleDeleteCharge = (chargeId: string) => {
    chargeId &&
      deleteRecurringCharge({
        variables: { deleteRecurringChargeId: chargeId },
      });
  };

  const handleDeleteScheduleTransaction = (transactionId: string) => {
    transactionId &&
      deleteScheduleTransaction({
        variables: { id: transactionId },
      });
  };

  const handleSchedulePaymentModal = ({
    state,
    subscriptionId,
    scheduledTransactionId,
    transactionDate,
  }: ISchecduledPayment) => {
    modals.openModal({
      closeOnClickOutside: true,
      withCloseButton: false,
      children: (
        <SchedulePayment
          state={state}
          clientId={clientId!}
          subscriptionId={subscriptionId}
          scheduledTransactionId={scheduledTransactionId}
          transactionDate={transactionDate}
          onRefetch={onRefetch}
          closeModal={() => modals.closeAll()}
        />
      ),
    });
  };

  const viewPolicy = (signedAgreementId: string) => {
    modals.openModal({
      children: <ViewPolicies signedAgreementId={signedAgreementId} />,
      withCloseButton: true,
      closeOnClickOutside: true,
      title: "View Policies",
      styles: {
        modal: {
          width: "auto",
          minWidth: "440px",
        },
        body: {
          minHeight: "fit-content",
        },
      },
    });
  };

  const fetchAgreementRequests = () => {
    if (clientId) {
      getAgreementRequests({
        variables: { clientId },
        fetchPolicy: "network-only",
      });
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchAgreementRequests();
    }
  }, [clientId]);

  return (
    <>
      <Flex
        align={"center"}
        justify={"space-between"}
        style={{ paddingBottom: "12.5px", borderBottom: "1px solid" }}
      >
        <Title order={4} color={color}>
          Agreements
        </Title>
        {isAllowWrite && (
          <ActionIcon
            size="lg"
            onClick={handleSignedAgreementModal}
            sx={{
              "&:hover": {
                backgroundColor: theme.colors.secondary[0],
              },
            }}
          >
            <IconCirclePlus color={color} />
          </ActionIcon>
        )}
      </Flex>

      {agreementData &&
        agreementData?.map((item, index) => {
          let totalCost = 0;
          const findRequest = agreementRequests?.agreementRequests?.find(
            (e) =>
              e.signedAgreement.id === item.id &&
              e?.alterationAgreementStatus ===
                AlterationAgreementStatus.Requested
          );

          item?.subscription.recurringCharges?.map(
            (charge) => (totalCost = totalCost + charge.cost)
          );
          return (
            <Flex
              style={{
                background: theme.colors.primary[6],
                marginTop: "15px",
                padding: "15px",
                borderRadius: "10px",
              }}
              direction={"column"}
              py={5}
              key={"agreement-" + item.id + index}
            >
              <Flex justify={"space-between"} align={"center"}>
                <Text
                  size={18}
                  fw={500}
                  color={theme.colors.primaryTextColor[6]}
                >
                  {item.agreement.name}
                </Text>
                <Flex>
                  <Text
                    size={14}
                    fw={400}
                    color={theme.colors.primaryTextColor[6]}
                  >
                    {item?.status} - {item?.subscription.paymentStatus}
                  </Text>
                  {isAllowWrite && (
                    <ActionIcon
                      ml={4}
                      size="sm"
                      variant="transparent"
                      onClick={() => {}}
                      sx={{
                        "&:hover": {
                          backgroundColor: theme.colors.primary[2],
                        },
                      }}
                    >
                      {item?.status === SignedAgreementStatus.Active ||
                      item?.status === SignedAgreementStatus.Frozen ? (
                        <BsThreeDotsVertical
                          color={theme.colors.primaryTextColor[6]}
                          onClick={() => {
                            handleOpenListMenu({
                              name: item.agreement.name,
                              id: item.id,
                            });
                          }}
                        />
                      ) : null}
                    </ActionIcon>
                  )}
                </Flex>

                <Transition
                  mounted={activeOption?.id === item.id}
                  transition="slide-left"
                  duration={200}
                  timingFunction="ease-in-out"
                >
                  {(styles) => (
                    <Flex
                      my={15}
                      justify="space-between"
                      px={10}
                      py={5}
                      bg={theme.colors.primary[4]}
                      style={{
                        ...styles,
                        position: "absolute",
                        display: "flex",
                        width: "45%",
                        marginRight: "25px",
                        borderRadius: "5px",
                      }}
                      ref={outsideClickRef}
                    >
                      {findRequest ? null : (
                        <ActionIcon
                          variant="light"
                          color="blue.4"
                          size="md"
                          my={4}
                          w="45%"
                          mah={25}
                          onClick={() => {
                            if (item?.status === SignedAgreementStatus.Frozen) {
                              updateAgreementStatus(item.id);
                            } else {
                              updateSignedAgreementStatus(
                                item.id,
                                item.agreement.isOrg
                                  ? currentLocation?.id!
                                  : item.location.id,
                                AlterationAgreementType.Freeze,
                                item.agreement.paymentFrequency
                              );
                            }
                          }}
                          sx={{
                            backgroundColor: theme.colors.primary[4],
                            "&:hover": {
                              backgroundColor: theme.colors.primary[6],
                            },
                          }}
                        >
                          <AiOutlinePauseCircle />
                          <Text mx={10}>
                            {item?.status === SignedAgreementStatus.Frozen
                              ? "Un-Freeze Agreement"
                              : "Freeze Agreement"}
                          </Text>
                        </ActionIcon>
                      )}
                      <ActionIcon
                        variant="light"
                        size="md"
                        color="red.4"
                        my={4}
                        w="45%"
                        mah={25}
                        onClick={() => {
                          updateSignedAgreementStatus(
                            item.id,
                            item.agreement.isOrg
                              ? currentLocation?.id!
                              : item.location.id,
                            AlterationAgreementType.Cancel,
                            item.agreement.paymentFrequency
                          );
                        }}
                        sx={{
                          backgroundColor: theme.colors.primary[4],
                          "&:hover": {
                            backgroundColor: theme.colors.primary[6],
                          },
                        }}
                      >
                        <>
                          <BiBlock size={21} />
                          <Text mx={10}>Cancel Agreement</Text>
                        </>
                      </ActionIcon>
                      <CloseButton
                        size="md"
                        c={theme.colors.light[5]}
                        onClick={() => setActiveOption(undefined)}
                        my={4}
                        sx={{
                          "&:hover": {
                            backgroundColor: theme.colors.primary[6],
                          },
                        }}
                      />
                    </Flex>
                  )}
                </Transition>
              </Flex>
              <Text
                size={14}
                fw={400}
                my={20}
                color={theme.colors.primaryTextColor[6]}
              >
                {item.agreement.description}
              </Text>

              <Accordion
                mb={10}
                styles={{
                  item: {
                    border: `1px solid ${theme.colors.primary[6]}`,
                    marginTop: 15,
                    borderRadius: "4px",
                    overflow: "hidden",
                  },
                  control: {
                    background: theme.colors.primary[4],
                    color: theme.colors.primaryTextColor[4],
                    padding: "5px 13px",
                    "&:hover": {
                      backgroundColor: `${theme.colors.primary[2]} !important`,
                    },
                  },
                  label: {
                    fontWeight: 500,
                    fontSize: 16,
                  },
                  panel: {
                    paddingTop: 10,
                    backgroundColor: theme.colors.secondary[6],
                    color: theme.colors.secondaryTextColor[6],
                  },
                }}
              >
                <Accordion.Item value={item.id}>
                  <Accordion.Control>
                    <Flex justify={"space-between"} align={"center"}>
                      <Text size={14} fw={500}>
                        Payment
                      </Text>
                    </Flex>
                  </Accordion.Control>

                  <Accordion.Panel>
                    <Box m={10}>
                      <Text size={12} fw={500}>
                        Term & Frequency
                      </Text>
                      <Flex
                        direction={"column"}
                        px={5}
                        my={5}
                        bg={theme.colors.secondary[6]}
                        style={{ borderRadius: 10 }}
                      >
                        <Flex
                          w="100%"
                          justify={"space-between"}
                          align={"center"}
                          py={5}
                          px={5}
                        >
                          <Text size={12} fw={400}>
                            Charges {item?.subscription?.paymentFrequency}
                          </Text>
                          <Flex align={"center"}>
                            <Text size={12} fw={400} mx={10}>
                              Next Payment{" "}
                              {item?.subscription?.nextDraftDate
                                ? new Date(
                                    item?.subscription?.nextDraftDate
                                  ).toLocaleDateString()
                                : "N/A"}
                            </Text>
                            {isAllowWrite && (
                              <IconPencil
                                color={theme.colors.secondaryTextColor[6]}
                                size={20}
                                onClick={() => {
                                  handleEditModal({
                                    modalId: EditModalsList.FREQUENCY.id,
                                    modalName: EditModalsList.FREQUENCY.title,
                                    agreement: item,
                                  });
                                }}
                              />
                            )}
                          </Flex>
                        </Flex>

                        <Flex
                          w="100%"
                          justify={"space-between"}
                          align={"center"}
                          py={5}
                          px={5}
                          bg={theme.colors.secondary[4]}
                          style={{
                            borderTop: `1px solid ${theme.colors.secondary[2]}`,
                            borderBottom: `1px solid ${theme.colors.secondary[2]}`,
                          }}
                        >
                          <Text size={12} fw={400}>
                            Charges to
                          </Text>
                          <Flex align={"center"}>
                            <Text size={12} fw={400} mx={10}>
                              {`${
                                item.paymentMethod?.type ===
                                PaymentMethodType.Card
                                  ? item.paymentMethod?.merchant
                                  : "Bank Account"
                              } ending in ${
                                item.paymentMethod
                                  ? item.paymentMethod?.mask?.substring(
                                      item.paymentMethod?.mask.length - 4
                                    )
                                  : "N/A"
                              }`}
                            </Text>
                            {isAllowWrite && (
                              <IconPencil
                                color={theme.colors.secondaryTextColor[6]}
                                size={20}
                                onClick={() => {
                                  handleEditModal({
                                    modalId: EditModalsList.PAYMENT.id,
                                    modalName: EditModalsList.PAYMENT.title,
                                    agreement: item,
                                  });
                                }}
                              />
                            )}
                          </Flex>
                        </Flex>

                        <Flex
                          w="100%"
                          justify={"space-between"}
                          align={"center"}
                          py={5}
                          px={5}
                        >
                          <Text size={12} fw={400}>
                            {`${item?.agreement?.Unit} ${item?.agreement?.termValue}`}
                          </Text>
                          <Flex align={"center"}>
                            <Text size={12} fw={400} mx={10}>
                              Expires{" "}
                              {item?.termEnd
                                ? new Date(item?.termEnd).toLocaleDateString()
                                : "N/A"}
                            </Text>
                            {isAllowWrite && (
                              <IconPencil
                                color={theme.colors.secondaryTextColor[6]}
                                size={20}
                                onClick={() => {
                                  handleEditModal({
                                    modalId: EditModalsList.EXPIRY.id,
                                    modalName: EditModalsList.EXPIRY.title,
                                    agreement: item,
                                  });
                                }}
                              />
                            )}
                          </Flex>
                        </Flex>
                      </Flex>
                    </Box>

                    <Box m={10}>
                      <Flex
                        w="100%"
                        justify={"space-between"}
                        align={"center"}
                        mt={10}
                      >
                        <Text size={12} fw={500}>
                          Recurring Fee
                        </Text>
                        <Flex>
                          <Text size={14} fw={600}>
                            {currencyFormatter.format(totalCost)}
                          </Text>
                          <Flex ml={10} mr={5}>
                            {isAllowWrite && (
                              <MdPlaylistAdd
                                size={25}
                                onClick={() => {
                                  handleRecurringChargesModal(
                                    CRUDSTATES.Create,
                                    item?.subscription?.id
                                  );
                                }}
                              />
                            )}
                          </Flex>
                        </Flex>
                      </Flex>

                      <Flex
                        direction={"column"}
                        px={10}
                        py={5}
                        my={5}
                        bg={theme.colors.secondary[6]}
                        style={{ borderRadius: 10 }}
                      >
                        {item?.subscription?.recurringCharges?.map(
                          (charge, index) => {
                            return (
                              <Flex
                                key={charge.id + index}
                                w="100%"
                                py={10}
                                style={{
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  background:
                                    index % 2 !== 0
                                      ? theme.colors.secondary[4]
                                      : "",
                                  borderTop:
                                    index % 2 !== 0
                                      ? `1px solid ${theme.colors.secondary[4]}`
                                      : "",
                                  borderBottom:
                                    index % 2 !== 0
                                      ? `1px solid ${theme.colors.secondary[4]}`
                                      : "",
                                }}
                              >
                                <Text size={12} fw={400}>
                                  {charge.name}
                                </Text>
                                <Flex align={"center"}>
                                  <Text size={12} fw={400} mx={10}>
                                    {`$ ${charge.cost}`}
                                  </Text>
                                  {isAllowWrite && (
                                    <RxDotsVertical
                                      color={theme.colors.secondaryTextColor[6]}
                                      size={20}
                                      onClick={() =>
                                        handleOpenListMenu({
                                          name: charge.name || "",
                                          id: charge.id,
                                        })
                                      }
                                    />
                                  )}
                                </Flex>
                                <Transition
                                  mounted={activeOption?.id === charge.id}
                                  transition="slide-left"
                                  duration={200}
                                  timingFunction="ease-in-out"
                                >
                                  {(styles) => (
                                    <Flex
                                      my={15}
                                      justify="space-between"
                                      px={10}
                                      py={5}
                                      bg={theme.colors[theme.colorScheme][0]}
                                      style={{
                                        ...styles,
                                        position: "absolute",
                                        width: "-webkit-fill-available",
                                        marginRight: "40px",
                                        borderRadius: "5px",
                                      }}
                                      ref={outsideClickRef}
                                    >
                                      <ActionIcon
                                        variant="light"
                                        size="md"
                                        color="blue.4"
                                        my={4}
                                        w="45%"
                                        mah={25}
                                        loading={false}
                                        onClick={() => {
                                          handleRecurringChargesModal(
                                            CRUDSTATES.Update,
                                            item?.subscription?.id,
                                            charge?.id
                                          );
                                        }}
                                      >
                                        <>
                                          <IconPencil size={21} />
                                          <Text mx={10}>Edit Charge</Text>
                                        </>
                                      </ActionIcon>
                                      <ActionIcon
                                        variant="light"
                                        size="md"
                                        color="red.4"
                                        my={4}
                                        w="45%"
                                        mah={25}
                                        loading={deleteLoading}
                                        onClick={() => {
                                          handleDeleteCharge(charge.id);
                                        }}
                                      >
                                        <>
                                          <BiBlock size={21} />
                                          <Text mx={10}>Delete Charge</Text>
                                        </>
                                      </ActionIcon>
                                      <CloseButton
                                        size="md"
                                        w={"10%"}
                                        c={theme.colors.light[5]}
                                        onClick={() =>
                                          setActiveOption(undefined)
                                        }
                                        my={4}
                                      />
                                    </Flex>
                                  )}
                                </Transition>
                              </Flex>
                            );
                          }
                        )}
                      </Flex>
                    </Box>
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>

              <Accordion
                styles={{
                  item: {
                    border: `1px solid ${theme.colors.primary[6]}`,
                    marginTop: 15,
                    borderRadius: "4px",
                    overflow: "hidden",
                  },
                  control: {
                    background: theme.colors.primary[4],
                    color: theme.colors.primaryTextColor[4],
                    padding: "5px 13px",
                    "&:hover": {
                      backgroundColor: `${theme.colors.primary[2]} !important`,
                    },
                  },
                  label: {
                    fontWeight: 500,
                    fontSize: 16,
                  },
                  panel: {
                    paddingTop: 10,
                    backgroundColor: theme.colors.secondary[6],
                    color: theme.colors.secondaryTextColor[6],
                  },
                }}
              >
                <Accordion.Item value={item.id}>
                  <Accordion.Control>
                    <Flex justify={"space-between"} align={"center"}>
                      <Text size={14} fw={500}>
                        Policies
                      </Text>
                    </Flex>
                  </Accordion.Control>
                  {item?.agreement.additionalPolicies ? (
                    <Accordion.Panel>
                      <Policies
                        data={item?.agreement.additionalPolicies}
                        onRefetch={onRefetch}
                        viewPolicy={() => viewPolicy(item.id)}
                      />
                      {/* <Box m={10}>
                        <Flex
                          direction={"column"}
                          my={5}
                          px={5}
                          bg={theme.colors.secondary[6]}
                          style={{ borderRadius: 10 }}
                        >
                          {item?.agreement.additionalPolicies?.map(
                            (policy, index) => {
                              return (
                                <Flex
                                  key={policy.name + index}
                                  w="100%"
                                  py={5}
                                  px={5}
                                  style={{
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    background:
                                      index % 2 !== 0
                                        ? theme.colors.secondary[4]
                                        : "",
                                    borderTop:
                                      index % 2 !== 0
                                        ? `1px solid ${theme.colors.secondary[2]}`
                                        : "",
                                    borderBottom:
                                      index % 2 !== 0
                                        ? `1px solid ${theme.colors.secondary[2]}`
                                        : "",
                                  }}
                                >
                                  <Text size={12} fw={400}>
                                    {`${policy?.name}`}
                                  </Text>
                                  <ActionIcon
                                    onClick={() => viewPolicy(item.id)}
                                  >
                                    <AiFillEye
                                      color={theme.colors.secondaryTextColor[6]}
                                    />
                                  </ActionIcon>
                                </Flex>
                              );
                            }
                          )}
                        </Flex>
                      </Box> */}
                    </Accordion.Panel>
                  ) : null}
                </Accordion.Item>
              </Accordion>

              <Accordion
                mb={10}
                styles={{
                  item: {
                    border: `1px solid ${theme.colors.primary[6]}`,
                    marginTop: 15,
                    borderRadius: "4px",
                    overflow: "hidden",
                  },
                  control: {
                    background: theme.colors.primary[4],
                    color: theme.colors.primaryTextColor[4],
                    padding: "5px 13px",
                    "&:hover": {
                      backgroundColor: `${theme.colors.primary[2]} !important`,
                    },
                  },
                  label: {
                    fontWeight: 500,
                    fontSize: 16,
                  },
                  panel: {
                    paddingTop: 10,
                    backgroundColor: theme.colors.secondary[6],
                    color: theme.colors.secondaryTextColor[6],
                  },
                }}
              >
                <Accordion.Item value={item.id}>
                  <Accordion.Control>
                    <Flex justify={"space-between"} align={"center"}>
                      <Text size={14} fw={500}>
                        Upcoming Payments
                      </Text>
                    </Flex>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <UpcomingPayments
                      data={item?.subscription}
                      onRefetch={onRefetch}
                      isAllowWrite={isAllowWrite}
                    />
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            </Flex>
          );
        })}
    </>
  );
};

export default memo(Agreements);
